import React, { useState, useMemo } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Plus, Trash2, ExternalLink, Box, Settings } from 'lucide-react';
import ComponentSelectionModal from '../modals/ComponentSelectionModal';
import { CreateComponentModal } from '../modals/CreateComponentModal';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { GhostButton } from '../ui/GhostButton';
import { useToast } from '../../context/ToastContext';
import { BASE_UPLOAD_URL } from '../../config';

import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';

const ComponentsTab = () => {
    const { toast } = useToast();
    const {
        productData,
        components,
        accessories, // Get accessories list to merge
        addComponent,
        deleteComponent,
        canEditContent,
        activeCharge // Add activeCharge
    } = usePIM();

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [deleteModalState, setDeleteModalState] = useState({ isOpen: false, componentId: null, type: null });

    // --- Data Merging Logic ---
    // User wants a "BOM" that shows EVERYTHING (Components + Accessories).
    // An item might exist in 'components' (product_components table), 'accessories' (product_accessories table), or both.
    // We merge them by the underlying Product ID (component_id / accessory_id) to create a unified view.

    const unifiedBOM = useMemo(() => {
        const bomMap = new Map();

        // 1. Process Components (Always include items explicitly added to BOM)
        components.forEach(c => {
            if (activeCharge && c.charge_id && c.charge_id !== activeCharge.id) return;

            const key = c.component_id;
            if (!bomMap.has(key)) {
                bomMap.set(key, {
                    id: key,
                    rowId: c.id,
                    sku: c.sku,
                    model_name: c.model_name,
                    image_url: c.image_url,
                    status: c.status,
                    status_color: c.status_color,
                    category: c.category,
                    product_types: c.product_types,

                    // Flags
                    in_components: true,
                    in_accessories: false,
                    is_package_content: !!c.is_package_content,

                    link_roles: [c.role]
                });
            } else {
                const item = bomMap.get(key);
                item.in_components = true;
                if (!item.link_roles.includes(c.role)) item.link_roles.push(c.role);
            }
        });

        // 2. Process Accessories (Merge in)
        accessories.forEach(a => {
            if (activeCharge && a.accessory_charge_id && a.accessory_charge_id !== activeCharge.id) return;

            const key = a.accessory_id;
            if (!bomMap.has(key)) {
                bomMap.set(key, {
                    id: key,
                    rowId: null,
                    sku: a.sku,
                    model_name: a.model_name,
                    image_url: a.image_url,
                    status: a.status,
                    status_color: a.status_color,
                    category: a.category,
                    product_types: a.product_types,

                    // Flags
                    in_components: false,
                    in_accessories: true,
                    is_package_content: !!a.is_package_content,

                    link_roles: ['accessory_link']
                });
            } else {
                const item = bomMap.get(key);
                item.in_accessories = true;
                item.is_package_content = item.is_package_content || !!a.is_package_content;
            }
        });

        // 3. Filter Logic (The "BOM" Definition)
        // SUPER STRICT Mode: Show item ONLY IF it has 'component' product type.
        // User Requirement: "Nur Geräte die als Komponenten deklariert wurden"
        // Spare parts are included ONLY if they also have 'component' type.

        return Array.from(bomMap.values()).filter(item => {
            let types = [];
            try {
                types = typeof item.product_types === 'string' ? JSON.parse(item.product_types) : (item.product_types || []);
            } catch (e) { types = []; }

            if (types.includes('component')) return true;

            return false;
        });
    }, [components, accessories, activeCharge]);

    // Sorting
    const { items: sortedBOM, requestSort, sortConfig } = useSortableData(unifiedBOM);

    const handleAddComponent = async (componentId, role) => {
        await addComponent(productData.id, componentId, role);
        setIsModalOpen(false);
    };

    const handleDeleteClick = (item) => {
        // If it's in components table, we delete from there. 
        // If it's ONLY in accessories table, we can't delete it from here (or we should warn user to remove from Accessories tab?)
        // User request: "Components tab is BOM...". 
        // For now, if it's in `components` table (has rowId), delete that. 
        if (item.in_components && item.rowId) {
            setDeleteModalState({ isOpen: true, componentId: item.rowId, type: 'component' });
        } else {
            toast.info("This item is added via Accessories tab. Please remove it from there.");
        }
    };

    const handleConfirmDelete = async () => {
        if (deleteModalState.componentId && deleteModalState.type === 'component') {
            await deleteComponent(productData.id, deleteModalState.componentId);
            setDeleteModalState({ isOpen: false, componentId: null, type: null });
        }
    };

    const handleCreateComponent = async (formData) => {
        try {
            // 1. Create Product
            const sku = formData.sku || `COMP-${Date.now().toString().slice(-6)}`;
            const payload = {
                model_name: formData.name,
                sku: sku,
                product_types: ['component'],
                category: formData.category || 'Component',
                status: formData.status || 'In Production',
                image_url: formData.image_url || null, // Handle image_url
                isAccessory: false // Explicitly NOT an accessory
            };

            const createRes = await fetch('/api/products', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify(payload)
            });

            if (!createRes.ok) throw new Error(await createRes.text());
            const newProd = await createRes.json();
            const newId = newProd.id || newProd.insertId || newProd.newProductId;

            // 2. Add as component
            if (newId) {
                await addComponent(productData.id, newId, formData.role || 'component_internal');
            }

            setIsCreateModalOpen(false);
        } catch (err) {
            console.error("Create component failed", err);
            toast.error("Failed to create component: " + err.message);
        }
    };

    const handleQuickCreate = () => setIsCreateModalOpen(true);

    return (
        <div className="h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-bold text-white">Components & BOM</h2>
                    <p className="text-xs text-stone-500 mt-1">Unified Bill of Materials (Includes Accessories, Spare Parts, and Internal Components).</p>
                </div>
                <div className="flex gap-2">
                    {canEditContent && (
                        <>
                            <GhostButton onClick={handleQuickCreate}>
                                <Plus size={16} /> Quick Create
                            </GhostButton>
                            <GhostButton onClick={() => setIsModalOpen(true)}>
                                <Plus size={16} /> Add Existing
                            </GhostButton>
                        </>
                    )}
                </div>
            </div>

            <div className="bg-black/20 border border-white/5 rounded-xl overflow-hidden shadow-xl flex-1 flex flex-col">
                <div className="overflow-y-auto flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-10">
                            <tr>
                                <SortableHeader label="Component / Part" sortKey="model_name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="SKU" sortKey="sku" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Roles & Tags" sortKey="role" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={requestSort} />
                                <th className="bg-black/40 text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-white/5 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-800">
                            {sortedBOM.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-stone-500">
                                        No components or accessories found.
                                    </td>
                                </tr>
                            ) : (
                                sortedBOM.map((comp) => (
                                    <tr key={comp.id} className="hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 bg-stone-800 rounded flex items-center justify-center text-stone-500 overflow-hidden shrink-0">
                                                    {comp.image_url ? (
                                                        <img src={`${BASE_UPLOAD_URL}/${comp.image_url.replace(/^\/+/, '')}`} alt="" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <Box size={16} />
                                                    )}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-stone-300 font-medium text-sm">{comp.model_name}</span>
                                                    {comp.category && <span className="text-[10px] text-stone-500">{comp.category}</span>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-stone-300 font-mono">{comp.sku}</td>
                                        <td className="px-4 py-2">
                                            <div className="flex flex-wrap gap-1.5">
                                                {/* Parse Product Types (Source of Truth for 'What it is') */}
                                                {(() => {
                                                    let types = [];
                                                    try {
                                                        types = typeof comp.product_types === 'string' ? JSON.parse(comp.product_types) : (comp.product_types || []);
                                                    } catch (e) { types = []; }

                                                    // Map specific known types to badges
                                                    const badges = [];
                                                    if (types.includes('accessory') || comp.in_accessories) badges.push({ label: 'Accessory', color: 'blue' });
                                                    if (types.includes('spare_part')) badges.push({ label: 'Spare Part', color: 'purple' });
                                                    if (types.includes('component')) badges.push({ label: 'Component', color: 'stone' });

                                                    // Deduplicate badges by label
                                                    const uniqueBadges = [...new Map(badges.map(item => [item.label, item])).values()];

                                                    if (uniqueBadges.length === 0) return <span className="text-stone-600 text-[10px]">-</span>;

                                                    return uniqueBadges.map(b => (
                                                        <span key={b.label} className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider border
                                                            ${b.color === 'blue' ? 'bg-blue-950/40 border-blue-800 text-blue-300' : ''}
                                                            ${b.color === 'purple' ? 'bg-purple-950/40 border-purple-800 text-purple-300' : ''}
                                                            ${b.color === 'stone' ? 'bg-stone-800/40 border-stone-700 text-stone-400' : ''}
                                                        `}>
                                                            {b.label}
                                                        </span>
                                                    ));
                                                })()}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm">
                                            {comp.status ? (
                                                <span
                                                    className="px-2 py-1 rounded text-[10px] font-medium border"
                                                    style={{
                                                        backgroundColor: `${comp.status_color || '#78716c'}33`,
                                                        color: '#fff',
                                                        borderColor: `${comp.status_color || '#78716c'}4d`
                                                    }}
                                                >
                                                    {comp.status}
                                                </span>
                                            ) : (
                                                <span className="text-stone-600 text-xs">-</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => window.open(`/?sku=${comp.sku}`, '_blank')}
                                                    className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-blue-400 hover:border-blue-900/50 hover:bg-blue-900/10 flex items-center justify-center transition"
                                                    title="View Product"
                                                >
                                                    <ExternalLink size={14} />
                                                </button>
                                                {canEditContent && comp.in_components && (
                                                    <button
                                                        onClick={() => handleDeleteClick(comp)}
                                                        className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-red-400 hover:border-red-900/50 hover:bg-red-900/10 flex items-center justify-center transition"
                                                        title="Remove from BOM"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ComponentSelectionModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onAdd={handleAddComponent}
            />

            <CreateComponentModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onCreate={handleCreateComponent}
            />

            <ConfirmationModal
                isOpen={deleteModalState.isOpen}
                onClose={() => setDeleteModalState({ isOpen: false, componentId: null, type: null })}
                onConfirm={handleConfirmDelete}
                title="Remove Component"
                message="Are you sure you want to remove this component? It will remain in the database as a product."
            />
        </div>
    );
};

export default ComponentsTab;
